/**
 * File: search_articles.tsx
 * Description: Admin search component that provides two linked Autocomplete dropdowns — one for
 * filtering by category and one for filtering by article — and notifies the parent of the
 * currently matching subset of categories via the onFilter callback.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  ListSubheader,
  List,
  useTheme,
} from '@mui/material'
import type { Category, ExtendedArticle } from '@/interfaces/category';
import { flattenCategoryArticles } from '@/utils/flatten_articles';
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";

/**
 * Props for the SearchArticles component.
 * @property {Category[]} categories - Full list of categories (with their articles) to search within.
 * @property {(filteredCategories: Category[]) => void} onFilter - Callback invoked whenever the active filter changes.
 */
interface SearchArticlesProps {
  categories: Category[];
  onFilter: (filteredCategories: Category[]) => void;
}

/**
 * Renders two linked Autocomplete fields that let the admin filter FAQ entries by category
 * and/or by article. Selecting an article automatically sets its parent category; clearing
 * the category also clears the article if it no longer matches. The parent is notified of
 * the filtered result on every selection change via onFilter.
 *
 * @param {Category[]} props.categories - Full list of categories to populate the dropdowns.
 * @param {(filteredCategories: Category[]) => void} props.onFilter - Called with the filtered categories whenever the selection changes.
 * @returns {JSX.Element}
 */
function SearchArticles({ categories, onFilter }: SearchArticlesProps) {

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<ExtendedArticle | null>(null);

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);

  /** Flatten all articles across every category into a single list for the article dropdown. */
  const allArticlesExtended = useMemo<ExtendedArticle[]>(() => flattenCategoryArticles(categories), [categories]);

  /** When a category is selected, restrict visible article options to that category only. */
  const filteredArticleOptions = useMemo<ExtendedArticle[]>(() =>
    selectedCategory
      ? allArticlesExtended.filter(article => article.categoryId === selectedCategory.id)
      : allArticlesExtended,
    [selectedCategory, allArticlesExtended]
  );

  /**
   * Handles category Autocomplete changes. Clears the selected article when the category is
   * removed or when the current article does not belong to the newly selected category.
   *
   * @param {React.SyntheticEvent} _event - The change event (unused).
   * @param {Category | null} newValue - The newly selected category, or null if cleared.
   */
  function handleCategoryChange(_event: React.SyntheticEvent, newValue: Category | null) {
    setSelectedCategory(newValue);
    if (!newValue || (selectedArticle && selectedArticle.categoryId !== newValue.id)) {
      setSelectedArticle(null);
    }
  }

  /**
   * Handles article Autocomplete changes. When an article is selected without a category,
   * automatically sets the category to the article's parent.
   *
   * @param {React.SyntheticEvent} _event - The change event (unused).
   * @param {ExtendedArticle | null} newValue - The newly selected article option, or null if cleared.
   */
  function handleArticleChange(_event: React.SyntheticEvent, newValue: ExtendedArticle | null) {
    setSelectedArticle(newValue);
    if (newValue && !selectedCategory) {
      const parentCategory = categories.find(category => category.id === newValue.categoryId);
      setSelectedCategory(parentCategory ?? null);
    }
  }

  useEffect(() => {
    if (!selectedCategory && !selectedArticle) {
      onFilter(categories);
    }
    else if (selectedCategory && !selectedArticle) {
      onFilter([selectedCategory]);
    }
    else if (selectedCategory && selectedArticle) {
      onFilter(
        [{
          ...selectedCategory,
          articles: [
            {
              id: selectedArticle.id,
              creation_date: selectedArticle.creation_date,
              title: selectedArticle.title,
              content: selectedArticle.content,
            }
          ]
        }]
      );
    }
  }, [selectedCategory, selectedArticle, categories, onFilter]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete<Category>
        options={categories}
        value={selectedCategory}
        onChange={handleCategoryChange}
        getOptionLabel={(category) => `${category.title} (${String(category.articles.length)})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar por Categoría"
            sx={{
              '& .MuiInputLabel-root': {
                  fontSize: {xs:12 , sm:15 },
                  fontWeight: 'bold'
                },
                'legend': {
                  fontSize: {xs:9.6 , sm:12 },
                  height: {xs:"7px" , sm:"10px" },
                }
              }}
          />
        )}
      />
      <Autocomplete<ExtendedArticle>
        options={filteredArticleOptions}
        value={selectedArticle}
        onChange={handleArticleChange}
        groupBy={(article) => article.categoryTitle}
        getOptionLabel={(article) => article.title}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar por Artículo"
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: {xs:12 , sm:15 },
                fontWeight: 'bold' },
                'legend': {
                  fontSize: {xs:9.6 , sm:12.3 },
                  height: {xs:"7px" , sm:"10px" },
                }
              }}
          />
        )}
        renderGroup={(params) => (
          <Box component="li" key={params.key}>
            <ListSubheader
              component="div"
              disableSticky={true}
              sx={{
                fontSize:15,
                background:colors.gray[900]
              }}
            >
              Categoria: {params.group}
            </ListSubheader>
            <List component="ul">
              {params.children}
            </List>
          </Box>
        )}
      />
    </Box>
  );
}

export default SearchArticles;
